import { Link } from 'react-router-dom';
import GuideLayout from '../../layouts/GuideLayout';
import type { TocItem } from '../../layouts/GuideLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { ResourceLink } from '../../components/content/ResourceLink';
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed';

const tocItems: TocItem[] = [
  { id: 'overview', label: 'Overview', level: 2 },
  { id: 'four-tiers', label: 'The Four Access Tiers', level: 2 },
  { id: 'redundancy', label: 'Redundancy Options', level: 2 },
  { id: 'operations', label: 'Operations Pricing', level: 2 },
  { id: 'pitr', label: 'Point-in-time Restore (PITR)', level: 2 },
  { id: 'certification', label: 'Certification Resources', level: 2 },
  { id: 'official-resources', label: 'Official Resources', level: 2 },
];

const bottomCta = (
  <AffiliateCTA
    programme="Whizlabs"
    courseName="AZ-104 Practice Tests"
    url="https://www.whizlabs.com/microsoft-azure-administrator-az-104/"
    price="From £15"
  />
);

export default function BlobStoragePricingUKPage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Azure Blob Storage Pricing UK 2026',
            description:
              'Azure Blob Storage Hot Cool Cold Archive tier pricing UK South GBP 2026.',
            author: { '@type': 'Organization', name: 'AzureCalc.uk' },
            publisher: {
              '@type': 'Organization',
              name: 'AzureCalc.uk',
              url: 'https://www.azure-calc.co.uk',
            },
            dateModified: '2026-03-30',
            url: 'https://www.azure-calc.co.uk/guides/azure-blob-storage-pricing-uk',
          }),
        }}
      />
      <GuideLayout
        title="Azure Blob Storage Pricing UK 2026"
        description="Azure Blob Storage pricing UK South 2026 in GBP. Hot vs Cool vs Cold vs Archive tier comparison, LRS vs GRS redundancy costs, operations pricing, and early deletion penalties. Updated March 2026."
        lastUpdated="March 2026"
        tocItems={tocItems}
        relatedCalculators={[
          { service: 'blob-storage', path: '/blob-storage', label: 'Blob Storage Calculator' },
        ]}
        bottomCta={bottomCta}
        canonical="https://www.azure-calc.co.uk/guides/azure-blob-storage-pricing-uk"
        metaTitle="Azure Blob Storage Pricing UK 2026 | AzureCalc.uk"
        metaDescription="Azure Blob Storage tier pricing UK South 2026 in GBP. Hot, Cool, Cold, Archive comparison, LRS vs GRS redundancy costs, and operations pricing."
      >
        <p className="text-[15px] italic mb-8" style={{ color: 'var(--text-tertiary)' }}>
          Hot, Cool, Cold and Archive tier costs for UK South in GBP. Redundancy options,
          operations pricing, and early deletion penalties explained.
        </p>

        <p className="text-[12px] italic mb-6" style={{ color: 'var(--text-tertiary)' }}>
          Built and verified by an independent Azure engineer, frustrated with the official calculator.
        </p>

        {/* SECTION 1 */}
        <section id="overview">
          <h2>Overview</h2>
          <p>
            Most Azure teams default to Hot storage and overpay. Hot costs approximately £0.018/GB/month;
            Cool costs approximately £0.010/GB/month — a 44% saving. For 10 TB of data that
            represents around £80/month saved, or nearly £1,000/year, just by moving blobs you access
            fewer than 18–20 times per month. The break-even point between Hot and Cool is roughly
            18–20 access days per month: access your data more frequently than that, and Hot is
            cheaper; access it less often, and Cool wins.
          </p>
          <p>
            Archive is a different trap entirely. It is roughly 20× cheaper per GB than Hot to store,
            but rehydration costs can erase years of savings in a single operation. If you restore a
            large Archive dataset more than once, you will almost certainly have paid more in retrieval
            fees than you would have spent keeping the data in Hot for 12 months. Archive makes
            economic sense only for data you are legally required to retain but virtually never need
            to read.
          </p>
          <p>
            All prices below are sourced from the Azure Retail Prices API for UK South in GBP and are
            updated nightly. Early deletion penalties, operations charges, and redundancy costs are all
            included in the calculator above.
          </p>
          <div className="not-prose mt-6">
            <CalculatorEmbed service="blob-storage" />
          </div>
        </section>

        {/* SECTION 2 */}
        <section id="four-tiers">
          <h2>The Four Access Tiers</h2>
          <div className="not-prose flex flex-col gap-4 my-6">
            {[
              {
                name: 'Hot',
                price: '~£0.016/GB/month',
                color: '#0078d4',
                bg: '#0a1929',
                border: '#0078d4',
                min: 'None',
                penalty: 'None',
                ops: 'Cheapest',
                retrieval: 'Free',
                points: [
                  'Highest storage cost, lowest access cost',
                  'No minimum retention period',
                  'Best for frequently accessed data, active application data, recently created files',
                ],
              },
              {
                name: 'Cool',
                price: '~£0.012/GB/month',
                color: '#50a0d4',
                bg: '#0a141f',
                border: '#404752',
                min: '30 days',
                penalty: 'Early deletion if removed before 30 days',
                ops: 'Higher than Hot',
                retrieval: 'Charged per GB',
                points: [
                  'Lower storage cost than Hot',
                  'Higher read/write operation costs',
                  'Best for infrequent access, backups, DR data, older datasets',
                ],
              },
              {
                name: 'Cold',
                price: '~£0.004/GB/month',
                color: '#50c8d4',
                bg: '#091415',
                border: '#404752',
                min: '90 days',
                penalty: 'Early deletion if removed before 90 days',
                ops: 'Significant cost',
                retrieval: 'Charged per GB',
                points: [
                  'Much lower storage cost',
                  'Significant operation costs',
                  'Best for rarely accessed compliance data, long-term backups',
                ],
              },
              {
                name: 'Archive',
                price: '~£0.001/GB/month',
                color: '#a0a0a0',
                bg: '#141414',
                border: '#404752',
                min: '180 days',
                penalty: 'Early deletion if removed before 180 days',
                ops: 'Most expensive',
                retrieval: 'Charged per GB + rehydration fee (takes hours)',
                points: [
                  'Cheapest storage — data is offline',
                  'Must rehydrate before reading (takes hours)',
                  'Best for data kept for compliance but almost never accessed',
                ],
              },
            ].map((tier) => (
              <div
                key={tier.name}
                className="rounded p-5"
                style={{ background: tier.bg, border: `1px solid ${tier.border}` }}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[15px] font-bold" style={{ color: tier.color }}>
                    {tier.name} Tier
                  </p>
                  <span
                    className="text-[13px] font-semibold px-2 py-0.5 rounded border"
                    style={{ color: tier.color, borderColor: tier.color, background: '#0a0e1a' }}
                  >
                    {tier.price}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[12px] mb-3">
                  {[
                    ['Min retention', tier.min],
                    ['Operations', tier.ops],
                    ['Retrieval', tier.retrieval],
                    ['Early deletion', tier.penalty],
                  ].map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <span className="shrink-0" style={{ color: 'var(--text-tertiary)' }}>{k}:</span>
                      <span style={{ color: 'var(--text-secondary)' }}>{v}</span>
                    </div>
                  ))}
                </div>
                <ul className="flex flex-col gap-1.5">
                  {tier.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                      <span style={{ color: tier.color }} className="shrink-0 mt-0.5">
                        ›
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="not-prose rounded p-4 my-6 bg-[#e8b000]/10 border border-[#e8b000]/40">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-[#e8b000] text-[20px] shrink-0 mt-0.5">
                warning
              </span>
              <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                <strong>Early deletion penalties:</strong> Moving data to a cooler tier then
                deleting it quickly can cost <em>more</em> than keeping it in Hot tier. Always
                model the full lifecycle cost before changing tiers on data you may delete soon.
              </p>
            </div>
          </div>

          <div className="not-prose mt-4">
            <CalculatorEmbed service="blob-storage" />
          </div>
        </section>

        {/* SECTION 3 */}
        <section id="redundancy">
          <h2>Redundancy Options</h2>
          <p>
            All four tiers are available with multiple redundancy levels. The redundancy option
            you choose affects price but not the tier billing model.
          </p>
          <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
            {[
              {
                name: 'LRS',
                full: 'Locally Redundant Storage',
                copies: '3 copies within one datacentre',
                cost: 'Cheapest',
                risk: 'Single datacentre failure loses data',
                best: 'Dev/test, data you can recreate',
              },
              {
                name: 'ZRS',
                full: 'Zone Redundant Storage',
                copies: '3 copies across 3 availability zones',
                cost: '~25% more than LRS',
                risk: 'Protects against datacentre failure',
                best: 'Production workloads needing high availability',
              },
              {
                name: 'GRS',
                full: 'Geo-Redundant Storage',
                copies: '6 copies — 3 in UK South, 3 in UK West',
                cost: '~2× more than LRS',
                risk: 'Protects against regional failure',
                best: 'Disaster recovery requirements',
              },
              {
                name: 'RA-GRS',
                full: 'Read-Access Geo-Redundant Storage',
                copies: 'Same as GRS — secondary is also readable',
                cost: 'Most expensive standard option',
                risk: 'Full regional failure protection',
                best: 'Compliance requiring readable secondary, global reads',
              },
            ].map((r) => (
              <div key={r.name} className="border rounded p-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[13px] font-black text-[#0078d4]">{r.name}</span>
                  <span className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{r.full}</span>
                </div>
                <div className="flex flex-col gap-1 text-[12px]">
                  {[
                    ['Copies', r.copies],
                    ['Cost', r.cost],
                    ['Protection', r.risk],
                    ['Best for', r.best],
                  ].map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <span className="w-16 shrink-0" style={{ color: 'var(--text-tertiary)' }}>{k}:</span>
                      <span style={{ color: 'var(--text-secondary)' }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p>
            For most UK production workloads ZRS is the right choice — it protects against
            datacentre-level failure without the cost of geo-replication. LRS is rarely appropriate
            for anything business-critical.
          </p>
        </section>

        {/* SECTION 4 */}
        <section id="operations">
          <h2>Operations Pricing</h2>
          <p>
            Operations are charged separately from storage, per 10,000 requests. The cost per
            operation increases as you move to cooler tiers — this is the mechanism that makes
            Archive tier cheap to store but expensive to access.
          </p>
          <div className="not-prose overflow-x-auto my-6">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                  <th className="text-left py-2 pr-4 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Operation</th>
                  <th className="text-right py-2 px-3 font-semibold text-[#0078d4]">Hot</th>
                  <th className="text-right py-2 px-3 font-semibold text-[#50a0d4]">Cool</th>
                  <th className="text-right py-2 px-3 font-semibold text-[#50c8d4]">Cold</th>
                  <th className="text-right py-2 pl-3 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Archive</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Write (per 10k)', 'Cheapest', 'Higher', 'Higher still', 'Most expensive'],
                  ['Read (per 10k)', 'Cheapest', 'Higher', 'Higher still', 'Most expensive'],
                  ['Data retrieval', 'Free', 'Charged/GB', 'Charged/GB', 'Charged/GB + rehydration'],
                  ['Rehydration fee', 'N/A', 'N/A', 'N/A', 'Applies'],
                ].map(([op, hot, cool, cold, archive]) => (
                  <tr key={op} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-2 pr-4" style={{ color: 'var(--text-secondary)' }}>{op}</td>
                    <td className="py-2 px-3 text-right" style={{ color: 'var(--text-tertiary)' }}>{hot}</td>
                    <td className="py-2 px-3 text-right" style={{ color: 'var(--text-tertiary)' }}>{cool}</td>
                    <td className="py-2 px-3 text-right" style={{ color: 'var(--text-tertiary)' }}>{cold}</td>
                    <td className="py-2 pl-3 text-right" style={{ color: 'var(--text-tertiary)' }}>{archive}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            <strong>For read-heavy workloads</strong> (CDN origin, media serving, application
            assets): Hot tier is almost always the cheapest total cost once you factor in read
            operation and retrieval charges.
          </p>
          <p>
            <strong>For write-heavy workloads with infrequent reads</strong> (logs, backups,
            event archives): Cool or Cold may save significant money. Always calculate total cost
            including operations, not just storage.
          </p>
        </section>

        {/* SECTION 5 */}
        <section id="pitr">
          <h2>Point-in-time Restore (PITR)</h2>
          <p>
            Point-in-time Restore (PITR) lets you restore a container or individual blobs to a
            previous state within a configurable retention window. It is useful for compliance
            requirements and protection against accidental deletion or corruption.
          </p>
          <p>
            PITR requires enabling blob versioning, soft delete, and change feed on your storage
            account — all three add cost. Change feed is charged per event logged, which adds up
            quickly for high-churn storage accounts. Restore itself is charged per GB restored.
            Model these costs before enabling PITR on high-volume accounts.
          </p>
        </section>

        {/* SECTION 6 */}
        <section id="certification">
          <h2>Certification Resources</h2>
          <p>
            Azure Blob Storage pricing and tier selection appears in both AZ-900 (fundamentals)
            and AZ-104 (administrator) exams.
          </p>
          <div className="not-prose flex flex-col gap-4 my-6">
            <AffiliateCTA
              programme="Pluralsight"
              courseName="AZ-900 Microsoft Azure Fundamentals — includes Storage module"
              url="https://www.pluralsight.com/paths/az-900-microsoft-azure-fundamentals"
              price="From £29/month"
              badgeText="Covers Storage Pricing"
            />
            <AffiliateCTA
              programme="Whizlabs"
              courseName="AZ-104 Practice Tests"
              url="https://www.whizlabs.com/microsoft-azure-administrator-az-104/"
              price="From £15"
            />
          </div>
        </section>

        {/* AZ-104 cross-link */}
        <div className="not-prose my-6">
          <Link
            to="/certifications/az-104-study-guide-uk"
            className="group flex items-center gap-4 px-5 py-4 rounded transition-colors"
            style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid rgba(0,120,212,0.5)' }}
          >
            <span className="material-symbols-outlined text-[#0078d4] text-[28px] shrink-0">workspace_premium</span>
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-semibold transition-colors group-hover:text-white" style={{ color: 'var(--text-primary)' }}>AZ-104 Study Guide UK →</p>
              <p className="text-[12px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>Domain 2 covers Blob Storage tiers, lifecycle policies, and access control in detail</p>
            </div>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px] shrink-0 group-hover:translate-x-0.5 transition-transform">arrow_forward</span>
          </Link>
        </div>

        {/* SECTION 7 */}
        <section id="official-resources">
          <h2>Official Resources</h2>
          <div className="not-prose flex flex-col gap-2 my-4">
            <ResourceLink
              title="Azure Blob Storage pricing"
              url="https://azure.microsoft.com/en-gb/pricing/details/storage/blobs/"
              source="Microsoft Azure"
            />
            <ResourceLink
              title="Blob storage access tiers overview"
              url="https://learn.microsoft.com/en-us/azure/storage/blobs/access-tiers-overview"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="Understanding block blob billing"
              url="https://learn.microsoft.com/en-us/azure/storage/blobs/storage-blob-block-blob-premium"
              source="Microsoft Learn"
            />
          </div>
        </section>
      </GuideLayout>
    </>
  );
}
